import cx from 'classnames'

import { SanityLinkFragment } from '@data/sanity/queries/types/link'
import { SanityMenuFeaturedLinkFragment } from '@data/sanity/queries/types/site'
import { useUser } from '@lib/auth'
import { getPageUrl, PageType } from '@lib/routes'

import AccountButton from '@components/buttons/account-button'
import LanguageSwitch from '@components/language-switch'
import CustomLink from '@components/link'
import Dropdown from './dropdown'
import DropdownButton from './dropdown-button'

export type ItemType = SanityLinkFragment | SanityMenuFeaturedLinkFragment

interface MenuItemProps {
  item: ItemType
  hasFocus: boolean
  useMegaNav: boolean
  onClick?: () => void
  isFooterMenuItem?: boolean
  isHeaderDesktopMenuItem?: boolean
  isHeaderMobilePrimaryMenuItem?: boolean
  isHeaderMobileSecondaryMenuItem?: boolean
  isMegaNavMenuItem?: boolean
}

const MenuItem = ({
  item,
  hasFocus,
  useMegaNav,
  onClick,
  isFooterMenuItem,
  isHeaderDesktopMenuItem,
  isHeaderMobilePrimaryMenuItem,
  isHeaderMobileSecondaryMenuItem,
  isMegaNavMenuItem,
}: MenuItemProps) => {
  const { user } = useUser()

  const title = 'title' in item && item.title ? (item.title as string) : ''

  const itemClassNames = cx({
    'inline-block py-2': isFooterMenuItem,
    'block uppercase font-semibold text-sm p-2': isHeaderDesktopMenuItem,
    'block p-0 text-4xl text-left py-1': isHeaderMobilePrimaryMenuItem,
    'block p-0 text-xl text-left': isHeaderMobileSecondaryMenuItem,
    'inline-block text-4xl leading-normal hover:opacity-40 transition-opacity duration-200':
      isMegaNavMenuItem,
  })

  // Language switch
  if (item._type === 'navLanguageSwitch') {
    return (
      <LanguageSwitch
        id={item._key}
        hasCurrency={!!item.displayCurrency}
        buttonClassName={itemClassNames}
      />
    )
  }

  // Account button
  if (item._type === 'navAccountButton') {
    const accountUrl = getPageUrl(
      user?.isLoggedIn ? PageType.ACCOUNT_PAGE : PageType.LOGIN_PAGE
    )

    return (
      <AccountButton
        id={`account-button-${item._key}`}
        href={accountUrl}
        showIcon={!!item.displayIcon}
        className={cx(itemClassNames, {
          'p-0': !item.text,
        })}
      >
        {item.text}
      </AccountButton>
    )
  }

  // Dropdown list
  if (item._type === 'navDropdown') {
    if (useMegaNav) {
      return (
        <DropdownButton
          id={item._key}
          title={title}
          className={itemClassNames}
        />
      )
    }

    if (!('dropdownItems' in item) || !item.dropdownItems) {
      return null
    }

    return (
      <Dropdown
        id={item._key}
        title={title}
        items={item.dropdownItems}
        onClick={onClick}
        listItemClassName={cx({
          'mx-2 my-0': isHeaderDesktopMenuItem,
        })}
        listItemLinkClassName={itemClassNames}
        buttonClassName={itemClassNames}
      />
    )
  }

  // Single link
  return (
    <CustomLink
      link={item}
      onClick={onClick}
      tabIndex={!hasFocus ? -1 : 0}
      className={itemClassNames}
      showCollectionCount={isMegaNavMenuItem}
    >
      {title}
    </CustomLink>
  )
}

export default MenuItem
